import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, firstValueFrom } from 'rxjs';
import { AuthorizationService } from 'services/authorization.service';
import { CentralServerService } from 'services/central-server.service';
import { LanguageService } from 'services/language.service';
import { MessageService } from 'services/message.service';
import { SpinnerService } from 'services/spinner.service';
import { WindowService } from 'services/window.service';
import { AbstractTabComponent } from 'shared/component/abstract-tab/abstract-tab.component';
import { ChargePointStatus, ChargingStation, Connector, QrCodeData } from 'types/ChargingStation';
import { ActionResponseQr } from 'types/DataResult';
import { RestResponse } from 'types/GlobalType';
import { User, UserNotifications, UserRole, UserStatus, UserWithPassword } from 'types/User';

import { Utils } from '../../utils/Utils';
import { SessionService } from './service/session.service';

@Component({
  selector: 'app-qrcode',
  templateUrl: 'qrcode.component.html',
  styleUrls: ['qrcode.component.scss'],
})
export class QrCodeComponent extends AbstractTabComponent implements OnInit, OnDestroy {
  public canListTags: boolean;
  public pricingDefinitions: any | null = null;
  public chargingStation: ChargingStation | null = null;
  public connectorId: number;
  public connectorExists = true;
  public connectorStatus: string | null = null;
  public browserLanguage: string;
  public connectorStatusColor: string;
  public chargingStationId: string;
  public decodedHeader: any;
  public connectorTest: Connector;
  public decodedPayload: any;
  public userName: string;
  public user: User;
  tokenData?: any;
  sessionToken: string;
  DecodedSessiontoken: any;
  codedConnectorID: string;
  QrCodeData: QrCodeData;
  codedChargingStationID: string;
  Sessiontoken: string;
  cardForm: FormGroup;
  intervalId: any;
  userRole: UserRole;
  isLoggedIn = false;
  userData: UserWithPassword;
  displayedColumns: string[] = [];
  token: any;
  createUserResponse: ActionResponseQr;
  dataLoaded = false;
  selectedLanguage = 'fr';
  tenantName = '';
  tenantLogo = '';

  public constructor(
    activatedRoute: ActivatedRoute,
    windowService: WindowService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private fb: FormBuilder,
    private centralServerService: CentralServerService,
    private spinnerService: SpinnerService,
    private languageService: LanguageService,
    private translateService: TranslateService,
    private authorizationService: AuthorizationService,
    private messageService: MessageService,
    private sessionService: SessionService
  ) {
    super(activatedRoute, windowService, ['all']);
    this.createForm();
  }

  get hasFlatFee(): boolean {
    return this.pricingDefinitions?.dimensions?.flatFee?.active === true && this.pricingDefinitions?.dimensions?.flatFee?.price != null;
  }
  get hasEnergy(): boolean {
    return this.pricingDefinitions?.dimensions?.energy?.active === true && this.pricingDefinitions?.dimensions?.energy?.price != null;
  }
  get hasChargingTime(): boolean {
    return this.pricingDefinitions?.dimensions?.chargingTime?.active === true && this.pricingDefinitions?.dimensions?.chargingTime?.price != null;
  }
  get hasParkingTime(): boolean {
    return this.pricingDefinitions?.dimensions?.parkingTime?.active === true && this.pricingDefinitions?.dimensions?.parkingTime?.price != null;
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  redirectToEv24(): void {
    const params = new URLSearchParams({
      tenantName: this.tenantName || '',
      chargingStationName: this.chargingStationId || '',
      connectorId: this.connectorId?.toString() || '',
    });
    window.location.href = `https://qr.ev24.support/?${params.toString()}`;
  }

  async redirectToPayment(): Promise<void> {
    // The QR flow requires a valid billing email before creating an account.
    if (this.cardForm.invalid) {
      this.cardForm.get('email').markAsTouched();
      this.messageService.showErrorMessage(this.translateService.instant('payment.invalide'));
      return;
    }

    if (this.sessionToken) {
      this.tokenData = Utils.decodeTokenHeader(this.sessionToken);
    }

    if ((this.codedChargingStationID && this.codedConnectorID) || (this.tokenData?.chargingStationID && this.tokenData?.connectorID)) {
      if (this.connectorTest?.status === 'Preparing') {
        try {
          await this.onSubmit();
          if (this.isLoggedIn) {
            const loggedUser = this.centralServerService.getLoggedUser();
            this.user = await firstValueFrom(this.centralServerService.getUser(loggedUser.id));
            this.sessionService.setLoggedUser(this.user);

            const chargingStationId = this.codedChargingStationID || this.tokenData.chargingStationID;
            const connectorId = this.codedConnectorID || this.tokenData.connectorID;

            void this.router.navigate([`/qrcode/${this.codedChargingStationID}/${this.codedConnectorID}/payment`]);
            this.spinnerService.hide();
          } else {
            console.log('No user logged in');
            this.messageService.showErrorMessage('User authentication required');
          }
        } catch (error) {
          console.error('Error during payment redirect:', error);
          this.messageService.showErrorMessage('Failed to proceed to payment');
          this.spinnerService.hide();
        }
      } else {
        this.handleConnectorStatus(this.connectorTest?.status);
      }
    } else {
      this.messageService.showErrorMessage('Invalid QR code data');
      this.spinnerService.hide();
    }
  }

  goBack(): void {
    this.location.back();
  }

  public changeLanguage(language: string): void {
    this.selectedLanguage = language;
    this.translateService.use(language);
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async ngOnInit(): Promise<void> {
    this.chargingStation = null;
    this.pricingDefinitions = null;

    // Get parameters from URL
    this.route.paramMap.subscribe(async (params) => {
      this.codedChargingStationID = params.get('codedChargingStationID');
      this.codedConnectorID = params.get('codedConnectorID');
      this.sessionToken = params.get('sessionToken');

      try {
        if (this.codedChargingStationID && this.codedConnectorID) {
          this.QrCodeData = await firstValueFrom(this.centralServerService.getQrCodeData(this.codedChargingStationID, this.codedConnectorID));
          this.token = this.QrCodeData.token;
          this.chargingStationId = this.QrCodeData.chargingStationID;
          this.connectorId = Number(this.QrCodeData.connectorID);
          this.loadTenantBrand();
          setTimeout(async () => this.checkLoginAndLoadData(), 2000);
        } else if (this.sessionToken) {
          this.DecodedSessiontoken = Utils.decodeTokenHeader(this.sessionToken);
          this.token = this.DecodedSessiontoken;
          this.chargingStationId = this.DecodedSessiontoken.chargingStationID;
          this.connectorId = Number(this.DecodedSessiontoken.connectorID);
          try {
            this.QrCodeData = await firstValueFrom(this.centralServerService.getQrCodeData(this.chargingStationId, this.DecodedSessiontoken.connectorID));
          } catch (error) {
            console.log('QrCodeData not found, generating new QR code:', error);
            const qrCodeResponse = await firstValueFrom(this.centralServerService.getConnectorQrCodeNoAuth(this.chargingStationId, this.connectorId));
            console.log(' QrCodeComponent ~ this.route.paramMap.subscribe ~ qrCodeResponse:', qrCodeResponse);
            this.QrCodeData = await firstValueFrom(this.centralServerService.getQrCodeData(this.chargingStationId, this.DecodedSessiontoken.connectorID));
          }
          this.codedChargingStationID = this.QrCodeData.codedChargingStationID;
          this.codedConnectorID = this.QrCodeData.codedConnectorID;
          this.loadTenantBrand();
          setTimeout(async () => this.checkLoginAndLoadData(), 2000);
        } else {
          console.error('Invalid URL parameters: Missing charging station ID or connector ID');
          this.connectorExists = false;
          this.messageService.showErrorMessage('Invalid QR code URL');
        }
      } catch (error) {
        console.error('Error processing QR code data:', error);
        this.connectorExists = false;
        this.messageService.showErrorMessage('Failed to process QR code');
      }
    });
  }

  createForm() {
    this.cardForm = this.fb.group({ email: ['', [Validators.required, Validators.email]] });
  }

  public createAllTrueNotifications = (): UserNotifications => ({
    sendSessionStarted: true, sendOptimalChargeReached: true, sendEndOfCharge: true, sendEndOfSession: true,
    sendUserAccountStatusChanged: true, sendNewRegisteredUser: true, sendUnknownUserBadged: true,
    sendChargingStationStatusError: true, sendChargingStationRegistered: true, sendOcpiPatchStatusError: true,
    sendOicpPatchStatusError: true, sendUserAccountInactivity: true, sendPreparingSessionNotStarted: true,
    sendOfflineChargingStations: true, sendBillingSynchronizationFailed: true, sendBillingPeriodicOperationFailed: true,
    sendSessionNotStarted: true, sendCarCatalogSynchronizationFailed: true, sendComputeAndApplyChargingProfilesFailed: true,
    sendEndUserErrorNotification: true, sendBillingNewInvoice: true, sendAdminAccountVerificationNotification: true,
  });

  public async onSubmit(): Promise<void> {
    try {
      const email = this.cardForm.get('email').value;
      let userCreatedOrLoggedIn = false;
      this.userName = Utils.getUsernameFromEmail(email);
      let DEFAULT_PASSWORD;
      this.spinnerService.hide();
      try {
        this.userName = Utils.getUsernameFromEmail(email);
        DEFAULT_PASSWORD = `${this.userName}@123A`;
        await this.createNewUser(email, DEFAULT_PASSWORD);
        userCreatedOrLoggedIn = true;
        this.spinnerService.hide();
      } catch (error: any) {
        const errorCode = error?.details?.errorCode || error?.error?.errorCode;
        this.spinnerService.hide();
        if (errorCode === 510) {
          try {
            this.userName = Utils.getUsernameFromEmail(email);
            DEFAULT_PASSWORD = `${this.userName}@123A`;
            await this.loginUser(email, DEFAULT_PASSWORD);
            userCreatedOrLoggedIn = true;
            this.spinnerService.hide();
          } catch (loginError) {
            const emailParts = email.split('@');
            const newEmail = `${emailParts[0]}+${this.getCurrentTimeForEmail()}@${emailParts[1]}`;
            await this.createNewUser(newEmail, DEFAULT_PASSWORD);
            userCreatedOrLoggedIn = true;
            this.spinnerService.hide();
          }
        } else {
          throw error;
        }
      }
    } catch (error) {
      this.messageService.showErrorMessage('An unexpected error occurred');
      console.error('Error during authentication flow:', error);
      this.spinnerService.hide();
    }
    this.spinnerService.hide();
  }

  public getChargingStationTariffID() {
    const ChargingStationID = this.chargingStation?.id;
    const ConnectorID = this.connectorId;
    const roaming = false;
    if (ChargingStationID && ConnectorID) {
      this.centralServerService.getChargingStationTariffQr(ChargingStationID, ConnectorID, roaming).subscribe({
        next: (data) => {
          this.pricingDefinitions = data;
        },
        error: (err) => {
          console.error('Error fetching tariff data:', err);
        },
      });
    }
  }

  private loadTenantBrand(): void {
    this.tenantName = this.QrCodeData?.tenantName || '';
    const subdomain = this.windowService.getSubdomain();
    if (subdomain) {
      this.centralServerService.getTenantLogoBySubdomain(subdomain).subscribe({
        next: (tenantLogo: string) => {
          if (tenantLogo) {
            this.tenantLogo = tenantLogo;
          }
        },
        error: () => { /* Keep brand text fallback */ },
      });
    }
  }

  private async createNewUser(email: string, password: string): Promise<void> {
    this.spinnerService.show();
    this.userData = {
      status: UserStatus.ACTIVE, firstName: this.chargingStationId, name: this.userName, email, locale: 'en_US',
      role: UserRole.QRCODE, id: '', issuer: false, fullName: '', tags: [], plateID: '', phone: undefined,
      mobile: '', notificationsActive: true, notifications: this.createAllTrueNotifications(), address: undefined,
      iNumber: '', costCenter: undefined, image: '', language: '', numberOfSites: 0, scopes: [], companies: [],
      sites: [], sitesAdmin: [], sitesOwner: [], userHashID: 0, tenantHashID: 0, eulaAcceptedHash: '',
      eulaAcceptedVersion: 0, eulaAcceptedOn: undefined, billingData: undefined, technical: false, freeAccess: false,
      password, acceptEula: true,
    };
    this.createUserResponse = await firstValueFrom(this.centralServerService.createUserQr(this.userData));
    this.spinnerService.hide();
    if (this.createUserResponse.status === RestResponse.SUCCESS) {
      console.log(`User created successfully with email: ${email}`);
      await this.loginUser(email, password);
      this.userData.id = this.createUserResponse.id;
      this.sessionService.setUserId(this.userData.id);
      this.sessionService.setLoggedUser(this.userData);
    }
    this.spinnerService.hide();
  }

  private async loginUser(email: string, password: string): Promise<void> {
    this.spinnerService.show();
    this.authorizationService.cleanUserAndUserAuthorization();
    const loginUser: UserWithPassword = {
      email, password, id: '', issuer: false, name: '', firstName: '', fullName: '', tags: [], plateID: '',
      phone: undefined, mobile: '', notificationsActive: false, notifications: this.createAllTrueNotifications(),
      address: undefined, iNumber: '', costCenter: false, status: UserStatus.ACTIVE, image: '', role: UserRole.QRCODE,
      locale: '', language: '', numberOfSites: 0, scopes: [], companies: [], sites: [], sitesAdmin: [], sitesOwner: [],
      userHashID: 0, tenantHashID: 0, eulaAcceptedHash: '', eulaAcceptedVersion: 0, eulaAcceptedOn: undefined,
      billingData: undefined, technical: false, freeAccess: false, acceptEula: true,
    };
    const result = await firstValueFrom(this.centralServerService.login(loginUser));
    this.isLoggedIn = true;
    this.centralServerService.loginSucceeded(result.token);
    const loggedUser = this.centralServerService.getLoggedUser();
    this.sessionService.setUserId(loggedUser.id);
    this.spinnerService.hide();
  }

  private getCurrentTimeForEmail(): string {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${hours}${minutes}${seconds}`;
  }

  private async checkLoginAndLoadData(): Promise<void> {
    this.authorizationService.getUserRole();
    this.setupLanguagePreferences();
    const entityId = this.chargingStationId;
    const connectorId = this.connectorId;
    if (entityId) {
      (await this.loadChargingStation(entityId, connectorId)).subscribe({
        next: (chargingStation: ChargingStation) => {
          this.getChargingStationTariffID();
          if (connectorId) {
            this.intervalId = setInterval(async () => (await this.loadChargingStation(entityId, connectorId)).subscribe(), 5000);
          } else {
            console.error('Connector ID is missing in the URL');
          }
        },
        error: (error) => {
          console.error('Failed to load charging station:', error);
        },
      });
    } else {
      console.error('Entity ID is missing in the URL');
    }
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  private async loadChargingStation(entityId: string, connectorId: number): Promise<Observable<ChargingStation>> {
    return new Observable((observer) => {
      this.centralServerService.getChargingStationQr(entityId).subscribe({
        next: (chargingStation: ChargingStation) => {
          this.chargingStation = chargingStation;
          this.connectorExists = chargingStation.connectors.some((connector) => connector.connectorId === connectorId);
          this.connectorId = connectorId;
          this.connectorTest = Utils.getConnectorFromID(chargingStation, connectorId);
          this.connectorStatus = this.connectorTest.status;
          if (this.connectorTest) {
            const statusKey = `connectorStatus.${this.connectorTest.status.toUpperCase()}` || this.connectorTest.status;
            this.translateService.get(statusKey).subscribe((translatedStatus: string) => {
              this.connectorStatusColor = Utils.applyStatusColor(this.connectorTest.status.toUpperCase());
              this.connectorExists = true;
              this.dataLoaded = true;
              observer.next(chargingStation);
              observer.complete();
            });
          } else {
            this.connectorExists = false;
            this.connectorStatus = '';
            this.dataLoaded = false;
            observer.error('Connector does not exist');
          }
          if (!this.connectorExists) {
            this.spinnerService.hide();
          }
        },
        error: (error) => {
          console.error('Failed to load charging station:', error);
          this.spinnerService.hide();
          this.connectorExists = false;
          this.connectorStatus = '';
          this.dataLoaded = false;
          observer.error(error);
        },
      });
    });
  }

  private handleConnectorStatus(status: string): void {
    switch (status) {
      case ChargePointStatus.CHARGING:
        this.messageService.showInfoMessage('Connector is already charging.');
        break;
      case ChargePointStatus.UNAVAILABLE:
        this.messageService.showErrorMessage('Connector is Unavailable.');
        break;
      case ChargePointStatus.OCCUPIED:
        this.messageService.showInfoMessage('Connector is Occupied.');
        break;
      case ChargePointStatus.SUSPENDED_EVSE:
        this.messageService.showWarningMessage('EVse suspended.');
        break;
      case ChargePointStatus.FAULTED:
        this.messageService.showErrorMessage('Connector is Faulted.');
        break;
      case ChargePointStatus.RESERVED:
        this.messageService.showInfoMessage('Connector is Reserved.');
        break;
      case ChargePointStatus.FINISHING:
        this.messageService.showWarningMessage('Please unplug the connector.');
        break;
      case ChargePointStatus.SUSPENDED_EV:
        this.messageService.showWarningMessage('EV Suspended');
        break;
      default:
        this.messageService.showWarningMessage('Connector must be plugged in your vehicle.');
    }
    this.spinnerService.hide();
  }

  private setupLanguagePreferences(): void {
    this.browserLanguage = this.languageService.getBrowserLanguage() || 'fr';
    const supportedLanguages = ['en', 'fr', 'es', 'de', 'it', 'pt', 'cs', 'cz'];
    const baseLanguage = this.browserLanguage.split('-')[0];
    const languageToUse = supportedLanguages.includes(baseLanguage) ? baseLanguage : 'en';
    this.selectedLanguage = languageToUse;
    this.translateService.use(languageToUse);
  }
}
